import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Observer, take } from 'rxjs';
import { AppUtilityService } from '../../services/app-utility.service';
import { apiPaths } from '../../../app/core/constants/api.const';
import { LotNumbers, PanelInfoResponse } from '../../models/panel.model';
import { PhaseTypeOptions } from '../../models/run.model';
import { StudyListItem, StudyListRequest, StudyListResponse } from '../../models/study.model';

interface UserInfo {
  first_name: string;
  last_name: string;
  email: string;
}

interface UsersResponse {
  users: (UserInfo & { is_active: boolean })[];
}

/**
 * StudyLinkPickerComponent
 *
 * A reusable picker for searching studies (by panel / phase / user) and selecting one or more to
 * link to the current study. The parent owns the source of truth: this component takes the current
 * set of linked run_names as input and emits the updated set whenever the user adds or removes a link.
 *
 * Studies are identified by `run_name` only (matches how load/delete already identify studies).
 *
 * Search reuses the existing `studies/display/` endpoint — no new backend endpoint is required.
 */
@Component({
  selector: 'app-study-link-picker',
  templateUrl: './study-link-picker.component.html',
  styleUrl: './study-link-picker.component.scss'
})
export class StudyLinkPickerComponent implements OnInit {
  /** run_name of the study being edited — excluded from results so it can't link to itself. */
  @Input() currentRunName = '';
  /** run_names already linked (pre-populates the chips + list selection state). */
  @Input() linkedRunNames: string[] = [];
  /** Emits the full linked set whenever it changes. */
  @Output() linkedRunNamesChange = new EventEmitter<string[]>();

  panels: LotNumbers[] = [];
  phaseTypes = PhaseTypeOptions;
  users: UserInfo[] = [];

  selectedPanelName: string | undefined;
  selectedPhaseType: string | undefined;
  selectedUserName: string | undefined;

  results: StudyListItem[] = [];
  filteredResults: StudyListItem[] = [];
  filterText = '';
  isSearching = false;
  hasSearched = false;
  errorMessage = '';

  constructor(private appUtilityService: AppUtilityService) { }

  ngOnInit(): void {
    this.loadPanels();
    this.loadUsers();
  }

  private loadPanels(): void {
    const observer = {
      next: (response: PanelInfoResponse) => {
        this.panels = response.panels;
      },
      error: (err: Error) => {
        this.panels = [];
        this.errorMessage = this.appUtilityService.getError(err).message;
      }
    } as Observer<PanelInfoResponse>;

    this.appUtilityService.get<PanelInfoResponse>(this.appUtilityService.getAPI(apiPaths.panelInfo.panels))
      .pipe(take(1))
      .subscribe(observer);
  }

  private loadUsers(): void {
    this.appUtilityService.get<UsersResponse>(this.appUtilityService.getAPI(apiPaths.users.getUserInfo))
      .pipe(take(1))
      .subscribe({
        next: response => {
          this.users = (response?.users ?? []).filter(user => user.is_active);
        },
        error: () => {
          this.users = [];
        }
      });
  }

  panelChangeAction(): void {
    this.search();
  }

  phaseChangeAction(): void {
    this.search();
  }

  userChangeAction(): void {
    this.search();
  }

  /** Runs the study search when both panel and phase are selected. */
  private search(): void {
    if (!this.selectedPanelName || !this.selectedPhaseType) {
      return;
    }

    this.isSearching = true;
    this.errorMessage = '';

    const request: StudyListRequest = {
      panel_name: this.selectedPanelName,
      workflow: this.normalizeWorkflowForDisplay(this.selectedPhaseType),
      user_name: this.selectedUserName ?? undefined
    };

    const observer = {
      next: (response: StudyListResponse) => {
        this.results = (response.studies ?? [])
          .filter(study => study.run_name !== this.currentRunName)
          .slice()
          .sort((a, b) => {
            if (!a.date && !b.date) return 0;
            if (!a.date) return 1;
            if (!b.date) return -1;
            return b.date.localeCompare(a.date);
          });
        this.filterText = '';
        this.hasSearched = true;
        this.applyFilter();
      },
      error: (err: Error) => {
        this.errorMessage = this.appUtilityService.getError(err).message;
        this.results = [];
        this.filteredResults = [];
        this.isSearching = false;
      },
      complete: () => {
        this.isSearching = false;
      }
    } as Observer<StudyListResponse>;

    this.appUtilityService.post<StudyListRequest, StudyListResponse>(
      this.appUtilityService.getAPI(apiPaths.study.studyList),
      request
    )
      .pipe(take(1))
      .subscribe(observer);
  }

  applyFilter(): void {
    const filterText = this.filterText.trim().toLowerCase();
    if (!filterText) {
      this.filteredResults = this.results;
      return;
    }

    this.filteredResults = this.results.filter(study => {
      const haystack = Object.values(study ?? {})
        .map(value => (value === null || value === undefined ? '' : String(value)))
        .join(' ')
        .toLowerCase();
      return haystack.includes(filterText);
    });
  }

  isLinked(runName: string): boolean {
    return this.linkedRunNames.includes(runName);
  }

  toggleLink(runName: string): void {
    const next = new Set(this.linkedRunNames);
    if (next.has(runName)) {
      next.delete(runName);
    } else {
      next.add(runName);
    }
    this.linkedRunNames = Array.from(next);
    this.linkedRunNamesChange.emit(this.linkedRunNames);
  }

  removeLink(runName: string): void {
    this.linkedRunNames = this.linkedRunNames.filter(name => name !== runName);
    this.linkedRunNamesChange.emit(this.linkedRunNames);
  }

  private normalizeWorkflowForDisplay(workflow: string | null | undefined): string {
    const normalized = (workflow ?? '').trim();
    if (!normalized) {
      return '';
    }
    if (normalized === 'Calibrator Adjustment') {
      return 'CalibratorAdjustment';
    }
    if (normalized === 'Control Assignment') {
      return 'ControlAssignment';
    }
    return normalized;
  }
}
